import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Plus, MoreVertical, Edit, Trash2, UserCog, Shield, Loader2, KeyRound, XCircle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { MetaCell } from '../components/comercial360/MetaCell';
import { ComercialLink } from '../components/comercial360/com360Shared';
import { AvatarUploadDialog } from '../components/AvatarUploadDialog';
import { avatarSrc } from '../lib/avatar';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const UsersList = () => {
  const { user: currentUser, isAdmin, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  
  const [users, setUsers] = useState([]);
  const [avatarUser, setAvatarUser] = useState(null);
  const canEditAvatars = ['super_admin', 'admin', 'gestor'].includes(currentUser?.role);
  const [teams, setTeams] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'comercial',
    team_id: '',
    partner_id: '',
    team_leader_id: '',
    commission_code: '',
    user_permissions: { can_send_alerts: false, can_send_recognition: false, alert_role_filter: [] },
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, teamsRes, partnersRes] = await Promise.all([
        axios.get(`${API_URL}/users`),
        axios.get(`${API_URL}/teams`),
        axios.get(`${API_URL}/partners`)
      ]);
      setUsers(usersRes.data);
      setTeams(teamsRes.data);
      setPartners(partnersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        role: user.role,
        team_id: user.team_id || '',
        partner_id: user.partner_id || '',
        team_leader_id: user.team_leader_id || '',
        commission_code: user.commission_code || '',
        user_permissions: {
          can_send_alerts: !!(user.user_permissions?.can_send_alerts),
          can_send_recognition: !!(user.user_permissions?.can_send_recognition),
          alert_role_filter: Array.isArray(user.user_permissions?.alert_role_filter) ? user.user_permissions.alert_role_filter : [],
        },
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'comercial',
        team_id: '',
        partner_id: '',
        commission_code: '',
        user_permissions: { can_send_alerts: false, can_send_recognition: false, alert_role_filter: [] },
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error('Nome e email são obrigatórios');
      return;
    }
    
    if (!editingUser && !formData.password) {
      toast.error('Password é obrigatória para novos utilizadores');
      return;
    }
    
    setSaving(true);
    try {
      if (editingUser) {
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password;
        await axios.put(`${API_URL}/users/${editingUser.id}`, updateData);
        toast.success('Utilizador atualizado');
      } else {
        await axios.post(`${API_URL}/users`, formData);
        toast.success('Utilizador criado');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja eliminar este utilizador?')) return;
    
    try {
      await axios.delete(`${API_URL}/users/${id}`);
      toast.success('Utilizador eliminado');
      fetchData();
    } catch (error) {
      toast.error('Erro ao eliminar utilizador');
    }
  };

  const handleResetPassword = async (user) => {
    const newPwd = window.prompt(`Definir nova password para ${user.name}:`);
    if (!newPwd) return;
    if (newPwd.length < 6) {
      toast.error('Password deve ter pelo menos 6 caracteres');
      return;
    }
    try {
      await axios.put(`${API_URL}/users/${user.id}/reset-password`, { new_password: newPwd });
      toast.success(`Password de ${user.name} reposta. Será pedido para alterar no próximo login.`);
    } catch (error) {
      toast.error('Erro ao repor password');
    }
  };

  const handleToggleActive = async (user) => {
    const newState = !user.active;
    const msg = newState ? `Ativar ${user.name}?` : `Desativar ${user.name}? Não poderá aceder ao CRM.`;
    if (!window.confirm(msg)) return;
    try {
      await axios.put(`${API_URL}/users/${user.id}`, { active: newState });
      toast.success(`${user.name} ${newState ? 'ativado' : 'desativado'}`);
      fetchData();
    } catch (error) {
      toast.error('Erro ao alterar estado');
    }
  };


  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRoleBadge = (role) => {
    const styles = {
      super_admin: 'bg-red-100 text-red-700',
      gestor: 'bg-orange-100 text-orange-700',
      admin: 'bg-purple-100 text-purple-700',
      parceiro: 'bg-amber-100 text-amber-700',
      chefe_equipa: 'bg-teal-100 text-teal-700',
      backoffice: 'bg-blue-100 text-blue-700',
      comercial: 'bg-slate-100 text-slate-600',
    };
    const labels = {
      super_admin: 'Super Admin',
      gestor: 'Gestor',
      admin: 'Admin',
      parceiro: 'Parceiro',
      chefe_equipa: 'Chefe Equipa',
      backoffice: 'Backoffice',
      comercial: 'Comercial',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[role] || 'bg-slate-100 text-slate-600'}`}>
        {labels[role] || role}
      </span>
    );
  };

  const getTeamName = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    return team?.name || '-';
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="users-list-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-outfit flex items-center gap-2">
              <UserCog className="w-6 h-6 text-[#2D3E6F]" />
              Utilizadores
            </h1>
            <p className="text-slate-500 mt-1">{users.length} utilizadores registados</p>
          </div>
          
          {isAdmin() && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  onClick={() => handleOpenDialog()}
                  className="bg-[#2D3E6F] hover:bg-[#1a2a4a] rounded-full gap-2"
                  data-testid="create-user-button"
                >
                  <Plus className="w-4 h-4" />
                  Novo Utilizador
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <form onSubmit={handleSubmit}>
                  <DialogHeader>
                    <DialogTitle className="font-outfit">
                      {editingUser ? 'Editar Utilizador' : 'Novo Utilizador'}
                    </DialogTitle>
                    <DialogDescription>
                      {editingUser ? 'Atualize os dados do utilizador' : 'Crie um novo utilizador no sistema'}
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="space-y-4 py-4">
                    <div>
                      <Label htmlFor="name">Nome *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Nome completo"
                        className="mt-1.5"
                        required
                        data-testid="user-name-input"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="email@exemplo.com"
                        className="mt-1.5"
                        required
                        data-testid="user-email-input"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="password">
                        Password {editingUser && '(deixe vazio para manter)'}
                      </Label>
                      <Input
                        id="password"
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder="••••••••"
                        className="mt-1.5"
                        required={!editingUser}
                        data-testid="user-password-input"
                      />
                    </div>
                    
                    <div>
                      <Label>Função</Label>
                      <Select
                        value={formData.role}
                        onValueChange={(v) => setFormData({ ...formData, role: v })}
                      >
                        <SelectTrigger className="mt-1.5" data-testid="user-role-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="comercial">Comercial</SelectItem>
                          <SelectItem value="chefe_equipa">Chefe de Equipa</SelectItem>
                          <SelectItem value="backoffice">Backoffice</SelectItem>
                          <SelectItem value="parceiro">Parceiro</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                          {isSuperAdmin() && <SelectItem value="gestor">Gestor</SelectItem>}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Parceiro</Label>
                      <Select
                        value={formData.partner_id || 'none'}
                        onValueChange={(v) => setFormData({ ...formData, partner_id: v === 'none' ? '' : v })}
                      >
                        <SelectTrigger className="mt-1.5" data-testid="user-partner-select">
                          <SelectValue placeholder="Selecionar parceiro" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sem parceiro</SelectItem>
                          {partners.map(p => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label>Equipa</Label>
                      <Select
                        value={formData.team_id || 'none'}
                        onValueChange={(v) => setFormData({ ...formData, team_id: v === 'none' ? '' : v })}
                      >
                        <SelectTrigger className="mt-1.5" data-testid="user-team-select">
                          <SelectValue placeholder="Selecionar equipa" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sem equipa</SelectItem>
                          {teams.map(team => (
                            <SelectItem key={team.id} value={team.id}>
                              {team.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Chefe de Equipa</Label>
                      <Select
                        value={formData.team_leader_id || 'none'}
                        onValueChange={(v) => setFormData({ ...formData, team_leader_id: v === 'none' ? '' : v })}
                      >
                        <SelectTrigger className="mt-1.5" data-testid="user-leader-select">
                          <SelectValue placeholder="Selecionar chefe de equipa" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sem chefe de equipa</SelectItem>
                          {users.filter(u => u.role === 'chefe_equipa').map(u => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Código Comissão</Label>
                      <Input
                        value={formData.commission_code}
                        onChange={(e) => setFormData({ ...formData, commission_code: e.target.value })}
                        placeholder="Opcional"
                        className="mt-1.5"
                        data-testid="user-commission-input"
                      />
                    </div>
                  </div>

                  {/* Permissões Especiais — só visível para super_admin */}
                  {isSuperAdmin() && (
                    <div className="mt-6 border-2 border-rose-100 bg-rose-50/50 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Shield className="w-4 h-4 text-rose-600" />
                        <h3 className="font-semibold text-sm text-rose-900">Permissões Especiais</h3>
                      </div>
                      <p className="text-xs text-slate-500 mb-3">
                        Super admin e gestor já têm estas permissões por defeito. Aqui podes ativar para outros utilizadores (ex: Daniela Tomás, Hugo Credível).
                      </p>
                      <div className="space-y-2.5">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!formData.user_permissions?.can_send_alerts}
                            onChange={(e) => setFormData({ ...formData, user_permissions: { ...(formData.user_permissions || {}), can_send_alerts: e.target.checked } })}
                            className="w-4 h-4 accent-rose-600"
                            data-testid="perm-can-send-alerts"
                          />
                          <span>🚨 Pode enviar <strong>Alertas Fortes</strong></span>
                        </label>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!formData.user_permissions?.can_send_recognition}
                            onChange={(e) => setFormData({ ...formData, user_permissions: { ...(formData.user_permissions || {}), can_send_recognition: e.target.checked } })}
                            className="w-4 h-4 accent-emerald-600"
                            data-testid="perm-can-send-recognition"
                          />
                          <span>🎉 Pode enviar <strong>Reconhecimentos</strong></span>
                        </label>
                      </div>
                      <div className="mt-4 pt-3 border-t border-rose-200">
                        <p className="text-xs font-semibold text-slate-700 mb-1">Roles autorizadas como destinatários</p>
                        <p className="text-[11px] text-slate-500 mb-2 leading-relaxed">
                          Lista <b>explícita</b> de quem este utilizador pode contactar.
                          Marca pelo menos uma role abaixo, caso contrário <b>não conseguirá enviar a ninguém</b>.
                          <br/>
                          <span className="text-slate-400">(Super Admin e Gestor estão sempre isentos desta restrição.)</span>
                        </p>
                        {(() => {
                          const sel = formData.user_permissions?.alert_role_filter || [];
                          const hasSendPerm = !!(formData.user_permissions?.can_send_alerts || formData.user_permissions?.can_send_recognition);
                          if (sel.length === 0) {
                            return (
                              <div className={`mb-2 px-2.5 py-1.5 rounded-md border text-[11px] font-medium flex items-center gap-1.5 ${hasSendPerm ? 'bg-rose-50 border-rose-300 text-rose-800' : 'bg-slate-50 border-slate-200 text-slate-600'}`}>
                                {hasSendPerm ? '⛔ Não pode enviar a ninguém — marca pelo menos uma role.' : 'ℹ️ Nenhuma role autorizada (sem efeito enquanto as opções acima estiverem desactivadas).'}
                              </div>
                            );
                          }
                          return (
                            <div className="mb-2 px-2.5 py-1.5 rounded-md bg-emerald-50 border border-emerald-200 text-[11px] text-emerald-800 font-medium flex items-center gap-1.5 flex-wrap">
                              ✅ Pode enviar a: {sel.map(r => r.replace('_', ' ')).join(', ')}
                            </div>
                          );
                        })()}
                        <div className="grid grid-cols-2 gap-2">
                          {['comercial', 'chefe_equipa', 'angariador', 'admin', 'parceiro', 'backoffice', 'gestor'].map(role => {
                            const filter = formData.user_permissions?.alert_role_filter || [];
                            const checked = filter.includes(role);
                            return (
                              <label key={role} className="flex items-center gap-2 text-xs cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => {
                                    const next = e.target.checked
                                      ? [...filter, role]
                                      : filter.filter(r => r !== role);
                                    setFormData({ ...formData, user_permissions: { ...(formData.user_permissions || {}), alert_role_filter: next } });
                                  }}
                                  className="w-3.5 h-3.5"
                                  data-testid={`perm-role-${role}`}
                                />
                                <span className="capitalize">{role.replace('_', ' ')}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setDialogOpen(false)}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      disabled={saving}
                      className="bg-[#2D3E6F] hover:bg-[#1a2a4a]"
                      data-testid="save-user-button"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          A guardar...
                        </>
                      ) : (
                        editingUser ? 'Guardar' : 'Criar'
                      )}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Table */}
        <Card className="rounded-2xl border-slate-100 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-slate-400">Nenhum utilizador encontrado</p>
            </div>
          ) : (
          <div className="overflow-x-auto">
            <Table className="min-w-[800px]">
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead className="font-semibold">Utilizador</TableHead>
                  <TableHead className="font-semibold">Email</TableHead>
                  <TableHead className="font-semibold">Função</TableHead>
                  <TableHead className="font-semibold">Parceiro</TableHead>
                  <TableHead className="font-semibold">Chefe Equipa</TableHead>
                  <TableHead className="font-semibold">Meta/mês</TableHead>
                  <TableHead className="font-semibold">Estado</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user, index) => (
                  <TableRow key={user.id} className="hover:bg-slate-50/50" data-testid={`user-row-${index}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className={`h-10 w-10 ${canEditAvatars ? 'cursor-pointer ring-offset-2 hover:ring-2 hover:ring-[#4DD4E8]' : ''}`} title={canEditAvatars ? 'Mudar foto' : undefined}
                          onClick={() => canEditAvatars && setAvatarUser(user)} data-testid={`user-avatar-${user.id}`}>
                          <AvatarImage src={avatarSrc(user.avatar_url)} className="object-cover" />
                          <AvatarFallback className="bg-[#4DD4E8]/20 text-[#0e7490] font-medium">
                            {getInitials(user.name)}
                          </AvatarFallback>
                        </Avatar>
                        {['comercial', 'chefe_equipa'].includes(user.role) ? <ComercialLink id={user.id} name={user.name} className="font-medium" testId={`user-360-link-${user.id}`} /> : <span className="font-medium">{user.name}</span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">{user.email}</TableCell>
                    <TableCell>{getRoleBadge(user.role)}</TableCell>
                    <TableCell className="text-slate-600">{user.partner_name || '-'}</TableCell>
                    <TableCell className="text-[#4DD4E8]">{user.team_leader_name || '-'}</TableCell>
                    <TableCell><MetaCell user={user} editable={['super_admin', 'gestor'].includes(currentUser?.role)} /></TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        user.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {user.active ? 'Ativo' : 'Inativo'}
                      </span>
                    </TableCell>
                    <TableCell>
                      {isAdmin() && user.id !== currentUser?.id && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleOpenDialog(user)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleResetPassword(user)}>
                              <KeyRound className="w-4 h-4 mr-2" />
                              Repor Password
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleToggleActive(user)}
                              className={user.active ? 'text-amber-600' : 'text-emerald-600'}
                            >
                              {user.active ? (
                                <><XCircle className="w-4 h-4 mr-2" /> Desativar</>
                              ) : (
                                <><CheckCircle className="w-4 h-4 mr-2" /> Ativar</>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDelete(user.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Eliminar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          )}
        </Card>
      </div>
      <AvatarUploadDialog open={!!avatarUser} onClose={() => setAvatarUser(null)} userId={avatarUser?.id} name={avatarUser?.name}
        currentUrl={avatarUser?.avatar_url} onSaved={() => fetchData()} />
    </Layout>
  );
};
